import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import keycloak from './keycloak';
import api from './api';
import theme from './theme';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import AccountDetail from './pages/AccountDetail';
import Payments from './pages/Payments';
import NewPayment from './pages/NewPayment';
import Beneficiaries from './pages/Beneficiaries';
import StandingOrders from './pages/StandingOrders';
import Profile from './pages/Profile';
import { Spinner } from './components/common';

function App() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [initError, setInitError] = useState(null);
  const refreshInterval = useRef(null);

  useEffect(() => {
    keycloak
      .init({
        onLoad: 'login-required',
        pkceMethod: 'S256',
        checkLoginIframe: false,
      })
      .then((auth) => {
        setAuthenticated(auth);
        setLoading(false);

        if (auth) {
          // Load profile
          api.get('/me').then(setProfile).catch(console.error);

          // Auto-refresh token every 60s
          refreshInterval.current = setInterval(() => {
            keycloak.updateToken(30).catch(() => keycloak.login());
          }, 60000);
        }
      })
      .catch((err) => {
        console.error('Keycloak init failed:', err);
        setInitError(err?.message || 'Authentication service unavailable');
        setLoading(false);
      });

    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    };
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: theme.colors.bgAlt }}>
        <div style={{ textAlign: 'center' }}>
          <Spinner size={36} />
          <div style={{ marginTop: '16px', color: theme.colors.textLight, fontSize: '14px' }}>Connecting securely...</div>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: theme.colors.bgAlt }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '40px' }}>
          <h2 style={{ color: theme.colors.danger }}>Connection Error</h2>
          <p style={{ color: theme.colors.textLight, fontSize: '14px' }}>
            Unable to connect to the authentication service. Please try again later.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 20px', background: theme.colors.primary, color: '#fff', border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: theme.colors.bgAlt }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Authentication required</h2>
          <button onClick={() => keycloak.login()} style={{ padding: '10px 20px', background: theme.colors.primary, color: '#fff', border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer' }}>
            Sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Layout profile={profile}>
        <Routes>
          <Route path="/" element={<Dashboard profile={profile} />} />
          <Route path="/accounts" element={<Accounts />} />
          <Route path="/accounts/:id" element={<AccountDetail />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/payments/new" element={<NewPayment />} />
          <Route path="/beneficiaries" element={<Beneficiaries />} />
          <Route path="/standing-orders" element={<StandingOrders />} />
          <Route path="/profile" element={<Profile profile={profile} />} />
        </Routes>
      </Layout>
    </BrowserRouter>
  );
}

export default App;
